import { css } from '@emotion/react';

/**
 * Shared style constants for scan export
 * Used by both the static HTML generator and React components
 */
export const SCAN_EXPORT_STYLES = {
  colors: {
    text: '#1D1D1D',
    textGray: '#898989',
    border: '#E0E0E0',
    backgroundGray: '#EDEDED',
    backgroundYellow: '#FFF9E6',
  },
  fonts: {
    regular: 'CircularXXWeb-Regular, sans-serif',
    bold: 'CircularXXWeb-Bold, sans-serif',
  },
  fontSize: {
    tiny: '10px',
    small: '12px',
    medium: '14px',
    large: '16px',
    xlarge: '24px',
  },
  spacing: {
    xs: '5px',
    small: '10px',
    medium: '20px',
    large: '40px',
  },
} as const;

/**
 * CSS stylesheet for static HTML export
 */
export const getExportStylesheet = () => `
  @font-face {
    font-family: "CircularXXWeb-Regular";
    src: url("/assets/CircularXXWeb-Regular-C7qe-7pg.woff") format("woff");
  }

  @font-face {
    font-family: "CircularXXWeb-Regular";
    src: url("/assets/CircularXXWeb-Regular-C2Km8yIq.woff2") format("woff2");
  }

  @font-face {
    font-family: "CircularXXWeb-Bold";
    src: url("/assets/CircularXXWeb-Bold-BthsUl6F.woff") format("woff");
  }

  @font-face {
    font-family: "CircularXXWeb-Bold";
    src: url("/assets/CircularXXWeb-Bold-6tPVDRzI.woff2") format("woff2");
  }

  body {
    color: ${SCAN_EXPORT_STYLES.colors.text};
    font-family: ${SCAN_EXPORT_STYLES.fonts.regular};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    padding: ${SCAN_EXPORT_STYLES.spacing.medium} ${SCAN_EXPORT_STYLES.spacing.large};
    margin: 0;
  }

  h1 {
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.xlarge};
    font-weight: 500;
  }

  .row-between {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .head-row {
    background-color: ${SCAN_EXPORT_STYLES.colors.backgroundGray};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.large};
    font-weight: 500;
    margin-bottom: ${SCAN_EXPORT_STYLES.spacing.medium};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  }

  #property-details {
    margin-bottom: ${SCAN_EXPORT_STYLES.spacing.medium};
    padding: 0 ${SCAN_EXPORT_STYLES.spacing.small};
  }

  .three-col-grid {
    display: grid;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
    grid-template-columns: repeat(3, 1fr);
  }

  .three-col-row > div {
    display: flex;
    flex-grow: 0;
    width: 33%;
  }

  .border-box {
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    display: flex;
    flex-direction: column;
    gap: ${SCAN_EXPORT_STYLES.spacing.xs};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  }

  .border-box-label {
    color: ${SCAN_EXPORT_STYLES.colors.textGray};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
  }

  .border-box-value {
    font-size: ${SCAN_EXPORT_STYLES.fontSize.medium};
  }

  .medium {
    font-weight: 500;
  }

  .bold {
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-weight: bold;
  }

  .tiny {
    font-size: ${SCAN_EXPORT_STYLES.fontSize.tiny};
  }

  .col-10 {
    display: flex;
    flex-direction: column;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
  }

  .underlined {
    border-bottom: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.medium};
    padding: ${SCAN_EXPORT_STYLES.spacing.small} 0;
  }

  .underlined:last-of-type {
    border-bottom-width: 0;
  }

  .page {
    break-after: page;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    table-layout: auto;
  }

  thead {
    background-color: ${SCAN_EXPORT_STYLES.colors.backgroundGray};
  }

  th {
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    font-weight: bold;
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
    text-align: left;
  }

  td {
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  }

  .nowrap {
    text-wrap: nowrap;
  }

  .group-page-title {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
  }

  .group-image {
    height: 200px;
    width: 100%;
    object-fit: contain;
    object-position: center;
  }

  .page {
    break-after: page;
  }

  .terms-footer {
    box-sizing: border-box;
    padding-right: 25%;
    position: relative;
  }

  .org-logo {
    align-items: center;
    bottom: 0;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    overflow: hidden;
    padding: 10px;
    position: absolute;
    right: 0;
    top: 0;
    width: 25%;
  }

  .org-logo svg {
    display: block;
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    width: auto;
  }

  .row-banner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 0px;
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    font-weight: bold;
    color: ${SCAN_EXPORT_STYLES.colors.text};
    border-bottom: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    border-right: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    z-index: 1;
    pointer-events: none;
    white-space: nowrap;
    background-color: white;
    box-sizing: border-box;
  }

  .row-banner span {
    font-size: 6px;
  }

  .line-item-row {
    position: relative;
  }

  .line-item-cell {
    padding-top: ${SCAN_EXPORT_STYLES.spacing.small};
    padding-bottom: 10px;
    line-height: 1.5;
  }

  .line-item-cell-overflow {
    padding-top: ${SCAN_EXPORT_STYLES.spacing.small};
    padding-bottom: 10px;
    overflow: visible;
    line-height: 1.5;
  }

  .row-banner-td {
    padding: 10px;
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    font-weight: bold;
    color: ${SCAN_EXPORT_STYLES.colors.text};
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    background-color: white;
  }

  .row-banner-td-punchlist {
    background-color: rgba(173, 216, 230, 0.3);
  }

  .row-banner-td-attention-summary {
    background-color: #fffacd;
  }

  .row-banner-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .change-type-badge {
    display: inline-block;
    border-radius: 4px;
    color: #000;
    padding-top: 3px;
  }

  .row-banner-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .row-banner-note {
    font-weight: normal;
    white-space: normal;
  }

  .row-banner-task-id {
    white-space: nowrap;
    margin-left: auto;
  }

  .row-banner-task-description {
    font-weight: normal;
    color: ${SCAN_EXPORT_STYLES.colors.textGray};
  }

  .row-banner-task-name {
    font-weight: bold;
  }

  .bordered-container {
    border: 3px solid ${SCAN_EXPORT_STYLES.colors.text};
    margin-bottom: ${SCAN_EXPORT_STYLES.spacing.large};
  }

  .black-header {
    background-color: ${SCAN_EXPORT_STYLES.colors.text};
    color: white;
    padding: 15px ${SCAN_EXPORT_STYLES.spacing.medium};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.large};
    font-weight: bold;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .black-header-summary {
    background-color: ${SCAN_EXPORT_STYLES.colors.text};
    color: white;
    padding: 15px ${SCAN_EXPORT_STYLES.spacing.medium};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.large};
    font-weight: bold;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${SCAN_EXPORT_STYLES.spacing.medium};
  }

  .header-strikethrough {
    text-decoration: line-through;
    opacity: 0.7;
  }

  .header-separator {
    color: white;
    opacity: 0.5;
  }

  .content-wrapper {
    padding: ${SCAN_EXPORT_STYLES.spacing.medium};
  }

  .changed-cell {
    background-color: #fffacd !important;
  }

  .strikethrough {
    text-decoration: line-through;
    color: #999;
    margin-right: 8px;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .info-label {
    font-size: 10px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .info-value {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .header-info-container {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  @media print {
    @page {
      size: letter landscape;
    }
    .page {
      page-break-before: always;
    }
    .bordered-container {
      margin-bottom: 0;
    }
  }
`;

/**
 * Emotion CSS styles for React components
 */
export const REACT_PRINT_STYLES = {
  body: css`
    color: ${SCAN_EXPORT_STYLES.colors.text};
    font-family: ${SCAN_EXPORT_STYLES.fonts.regular};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    padding: ${SCAN_EXPORT_STYLES.spacing.medium} ${SCAN_EXPORT_STYLES.spacing.large};
    margin: 0;
    background: white;

    @media print {
      @page {
        size: letter landscape;
      }
    }
  `,

  h1: css`
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.xlarge};
    font-weight: 600;
    margin: 0;
  `,

  rowBetween: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,

  headRow: css`
    background-color: ${SCAN_EXPORT_STYLES.colors.backgroundGray};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.large};
    font-weight: 500;
    margin-bottom: ${SCAN_EXPORT_STYLES.spacing.medium};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  `,

  threeColGrid: css`
    display: grid;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
    grid-template-columns: repeat(3, 1fr);
  `,

  borderBox: css`
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    display: flex;
    flex-direction: column;
    gap: ${SCAN_EXPORT_STYLES.spacing.xs};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  `,

  borderBoxLabel: css`
    color: ${SCAN_EXPORT_STYLES.colors.textGray};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
  `,

  borderBoxValue: css`
    font-size: ${SCAN_EXPORT_STYLES.fontSize.medium};
  `,

  col10: css`
    display: flex;
    flex-direction: column;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
  `,

  underlined: css`
    border-bottom: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.medium};
    padding: ${SCAN_EXPORT_STYLES.spacing.small} 0;

    &:last-of-type {
      border-bottom-width: 0;
    }
  `,

  page: css`
    break-after: page;

    @media print {
      page-break-before: always;
    }
  `,

  table: css`
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    table-layout: auto;
  `,

  thead: css`
    background-color: ${SCAN_EXPORT_STYLES.colors.backgroundGray};
  `,

  th: css`
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    font-weight: bold;
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
    text-align: left;
  `,

  td: css`
    border: solid 1px ${SCAN_EXPORT_STYLES.colors.border};
    font-size: ${SCAN_EXPORT_STYLES.fontSize.small};
    padding: ${SCAN_EXPORT_STYLES.spacing.small};
  `,

  groupPageTitle: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${SCAN_EXPORT_STYLES.spacing.small};
  `,

  groupImage: css`
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    height: 200px;
    width: 100%;
  `,

  bold: css`
    font-family: ${SCAN_EXPORT_STYLES.fonts.bold};
    font-weight: 600;
  `,

  tiny: css`
    font-size: ${SCAN_EXPORT_STYLES.fontSize.tiny};
  `,

  nowrap: css`
    text-wrap: nowrap;
  `,

  termsFooter: css`
    box-sizing: border-box;
    padding-right: 25%;
    position: relative;
  `,

  orgLogo: css`
    align-items: center;
    bottom: 0;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    overflow: hidden;
    padding: 10px;
    position: absolute;
    right: 0;
    top: 0;
    width: 25%;

    svg {
      display: block;
      max-width: 100%;
      max-height: 100%;
      height: 100%;
      width: auto;
    }
  `,
};
